import {
    drawImageInBorderedCircle,
    drawImageInCircle,
    drawTiledBackgroundImage,
    scaleHeight,
    scaleWidth,
    hopefullyRoundRect,
    initCanvas,
    promiseFromNullable,
    wrapText,
} from './utils.js';
import { projectFormatter } from '../projectFormatter.js';

import { DEFAULT_LINE_HEIGHT, TEXT_HEIGHT_MULTIPLIER } from './layoutConstants.js';


// "Option 1" in the demo.
export const bubblyFillCanvas = (
    canvas,

    classroomPhotoUrl,
    teacherProfilePhotoUrl,
    formattedTeacherName,

    fulfillmentTrailer,
    projectCostToComplete,
    totalProjectCost,
    onSuccess,
) => {
    let ctx = initCanvas(canvas);

    const { logoUrl } = getUrlConstants();

    // Dark blue background covering full canvas
    ctx.fillStyle = '#2826b3';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // We have to preload the images if we want to be able to decide what order we draw them in.
    // And we do want that - otherwise we can't reliably put the profile photo on top of the classroom photo.
    let classroomPhotoPromise = promiseFromNullable(classroomPhotoUrl);
    let teacherProfilePhotoPromise = promiseFromNullable(teacherProfilePhotoUrl);
    let logoPromise = promiseFromNullable(logoUrl);

    Promise.all([classroomPhotoPromise, teacherProfilePhotoPromise, logoPromise])
        .then((images) => {
            // Top 1/4 of the canvas is a classroom photo (if available)
            if (images[0] != null) {
                let classroomPhoto = images[0];

                // Specify source-start-coords and source-height because we're only using the middle 1/3 of the image.
                ctx.drawImage(classroomPhoto, 0, Math.floor(classroomPhoto.height / 3), classroomPhoto.width, Math.floor(classroomPhoto.height / 3),
                    0, 0, canvas.getBoundingClientRect().width, Math.floor(canvas.getBoundingClientRect().height / 4));
            }

            if (images[1] != null) {
                let teacherProfilePhoto = images[1];

                // In the demo the full width is 270 px and the full height is 400px
                // and the center is at (45, 85) with radius 25.
                //
                // So: match those proportions?
                let x = scaleWidth(45, canvas);
                let y = scaleHeight(100, canvas);
                let radius = scaleWidth(25, canvas);

                drawImageInBorderedCircle(ctx, teacherProfilePhoto, x, y, radius);
            }

            if (images[2] != null) {
                let logo = images[2];

                let x = scaleWidth(17, canvas);
                let y = scaleHeight(350, canvas);
                // We *shouldn't* canvas-scale these because the shape of the logo should stay the same
                // regardless of the canvas.
                let logoWidth = 38;
                let logoHeight = 15;

                ctx.drawImage(logo, x, y, logoWidth, logoHeight);

                // Add the "+"
                ctx.fillStyle = '#ffffff';
                const lineHeight = DEFAULT_LINE_HEIGHT;
                ctx.font = `bold ${lineHeight}px Helvetica`;

                let plusSpace = scaleWidth(8, canvas);
                let plusCoords = {
                    x: Math.floor(x + logoWidth + plusSpace),
                    y: y + Math.floor(logoHeight * (7 / 8)),
                };
                ctx.fillText('+', plusCoords.x, plusCoords.y);
                let plusWidth = ctx.measureText('+').width;

                // Add the teacher-name
                ctx.fillStyle = '#ffbe36';
                let nameCoords = {
                    x: plusCoords.x + plusWidth + plusSpace,
                    y: plusCoords.y,
                };
                ctx.fillText(formattedTeacherName, nameCoords.x, nameCoords.y);
            }

            onSuccess(canvas);
        });


    const lineHeight = DEFAULT_LINE_HEIGHT;
    ctx.font = `${lineHeight}px Helvetica`;
    let formattedText = wrapText(ctx, fulfillmentTrailer, scaleWidth(17, canvas), lineHeight + scaleHeight(135, canvas), scaleWidth(250, canvas), Math.floor(lineHeight * TEXT_HEIGHT_MULTIPLIER));

    formattedText[0].forEach(function(item, index) {
        // Draw the background-rounded-rectangles.
        ctx.fillStyle = '#ffbe36';
        const textSize = ctx.measureText(item[0]);

        const horizontalPadding = Math.floor(lineHeight / 4);
        const x = Math.floor(item[1] - horizontalPadding);
        // Subtract lineHeight because rectangles specify upper left corner and text specifies bottom left
        const y = item[2] - lineHeight;

        // Last arg is rounding-radius. 0 = a normal rectangular corner. 0 is correct on lines other than the first and last.
        const upperLeftRadius = index === 0 ? 20 : 0;
        const lowerLeftRadius = (index === formattedText[0].length - 1) ? 20 : 0;
        hopefullyRoundRect(ctx, x, y, textSize.width + (2 * horizontalPadding), Math.floor(lineHeight * TEXT_HEIGHT_MULTIPLIER), [upperLeftRadius, 20, 20, lowerLeftRadius]);
        ctx.fill();
    });

    formattedText[0].forEach(function(item) {
        ctx.fillStyle = '#161253';
        ctx.fillText(item[0], item[1], item[2]);
    });
}

/**
 * "Option 2" from our demos page.
 *
 * @param canvas
 * @param teacherProfilePhotoUrl
 * @param formattedTeacherName
 * @param fulfillmentTrailer
 * @param logoUrl
 * @param projectCostToComplete
 * @param totalProjectCost
 * @param backgroundPhotoUrl
 */
export const tinyCardFillCanvas = (
    canvas,

    classroomPhotoUrl,
    teacherProfilePhotoUrl,
    formattedTeacherName,

    fulfillmentTrailer,
    projectCostToComplete,
    totalProjectCost,
    onSuccess,
) => {
    let ctx = initCanvas(canvas);

    const { logoUrl, backgroundPhotoUrl } = getUrlConstants();

    // Dark blue background covering full canvas
    ctx.fillStyle = '#2826b3';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // We have to preload the images if we want to be able to decide what order we draw them in.
    // And we do want that - otherwise we can't reliably put the profile photo on top of the classroom photo.
    let backgroundPhotoPromise = promiseFromNullable(backgroundPhotoUrl);
    let teacherProfilePhotoPromise = promiseFromNullable(teacherProfilePhotoUrl);
    let logoPromise = promiseFromNullable(logoUrl);

    Promise.all([backgroundPhotoPromise, teacherProfilePhotoPromise, logoPromise])
        .then((images) => {
            // Start by drawing the background image!
            if (images[0] != null) {
                const backgroundPhoto = images[0];
                drawTiledBackgroundImage(ctx, backgroundPhoto, canvas);
            }

            // Draw "card" white block
            ctx.fillStyle = '#ffffff';
            const cardCoords = {
                x: scaleWidth(30, canvas),
                y: scaleHeight(100, canvas),
                height: scaleHeight(165, canvas),
            };
            cardCoords.width = canvas.getBoundingClientRect().width - (2 * cardCoords.x);
            hopefullyRoundRect(ctx, cardCoords.x, cardCoords.y, cardCoords.width, cardCoords.height, [10, 10, 10, 10]);
            ctx.fill();

            // Draw profile photo
            const profilePhotoCoords = {
                x: scaleWidth(65, canvas),
                y: scaleHeight(125, canvas),
                radius: scaleWidth(18, canvas),
            };
            if (images[1] != null) {
                const teacherProfilePhoto = images[1];

                drawImageInCircle(ctx, teacherProfilePhoto, profilePhotoCoords.x, profilePhotoCoords.y, profilePhotoCoords.radius);
            }

            // Add teacher-name
            ctx.fillStyle = '#2826b3';
            const lineHeight = DEFAULT_LINE_HEIGHT;
            ctx.font = `bold ${Math.floor(lineHeight * 0.75)}px Helvetica`;
            let nameCoords = {
                x: Math.floor(profilePhotoCoords.x + profilePhotoCoords.radius + lineHeight / 2),
                y: profilePhotoCoords.y + Math.floor(lineHeight / 4),
            };
            ctx.fillText(formattedTeacherName, nameCoords.x, nameCoords.y);

            // Add help-my-students text
            ctx.fillStyle = '#1d1d1d';
            ctx.font = `${lineHeight}px Helvetica`;
            let textCoords = {
                x: profilePhotoCoords.x - profilePhotoCoords.radius,
                y: profilePhotoCoords.y + profilePhotoCoords.radius + Math.floor(lineHeight * TEXT_HEIGHT_MULTIPLIER),
                cutoff: cardCoords.y + cardCoords.height,
            };
            let formattedText = wrapText(ctx, fulfillmentTrailer, textCoords.x, textCoords.y, canvas.getBoundingClientRect().width - 2 * textCoords.x, Math.floor(lineHeight * TEXT_HEIGHT_MULTIPLIER));
            formattedText[0].forEach(function(item, index) {
                // If this line won't fit, bail out.
                if (item[2] > textCoords.cutoff) {
                    return;
                }
                // If this is the last line that'll fit, add an ellipse to the end of the line.
                if (index < formattedText[0].length - 1 && formattedText[0][index + 1][2] > textCoords.cutoff) {
                    // Remove trailing space if applicable
                    item[0] = item[0].replace(/\s$/, '');
                    item[0] += '...';
                }

                ctx.fillText(item[0], item[1], item[2]);
            });

            if (images[2] != null) {
                const logoImage = images[2];

                const logoCoords = {
                    x: cardCoords.x,
                    y: scaleHeight(280, canvas),
                    // Don't scale width/height because we want to keep the logo proportions constant regardless of context.
                    width: 55,
                    height: 20,
                };

                ctx.drawImage(logoImage, logoCoords.x, logoCoords.y, logoCoords.width, logoCoords.height);
            }

            const progressBarData = {
                backgroundColor: '#efefef',
                foregroundColor: '#2c9be2',
                x: scaleWidth(115, canvas),
                y: scaleHeight(280, canvas),
                width: scaleWidth(120, canvas),
                height: scaleHeight(5, canvas),
                radius: scaleHeight(10, canvas),
                fullFraction: (totalProjectCost - projectCostToComplete) / totalProjectCost,
            }

            // Draw progress-bar-background
            ctx.fillStyle = progressBarData.backgroundColor;
            // Without this, we end up `fill`ing the previous shape as well. Weird!
            ctx.beginPath();
            // 10-pixel radius LGTM; not sure if that would need to scale with canvas.
            hopefullyRoundRect(ctx, progressBarData.x, progressBarData.y, progressBarData.width, progressBarData.height, [10, 10, 10, 10]);
            ctx.fill();

            // Draw progress-bar-foreground
            ctx.fillStyle = progressBarData.foregroundColor;
            // Without this, we end up `fill`ing the previous shape as well.
            ctx.beginPath();
            hopefullyRoundRect(ctx, progressBarData.x, progressBarData.y, Math.floor(progressBarData.width * progressBarData.fullFraction), progressBarData.height, [10, 10, 10, 10]);
            ctx.fill();

            const someMoneyString = projectFormatter.formatCurrency(projectCostToComplete);
            const amountRemainingLineData = {
                content: `${someMoneyString} Still Needed!`,
                foregroundColor: '#ffffff',
                x: progressBarData.x,
                y: progressBarData.y + Math.floor(lineHeight * 1.25),
                height: Math.floor(lineHeight * 0.75),
            }

            ctx.fillStyle = amountRemainingLineData.foregroundColor;
            ctx.font = `bold ${amountRemainingLineData.height}px Helvetica`;
            ctx.fillText(amountRemainingLineData.content, amountRemainingLineData.x, amountRemainingLineData.y);

            onSuccess(canvas);
        });
}

// "Option 3" only for HLHM. used bubbly version as a template, this is to be removed after the campaign
export const HLHMCanvas = (
    canvas,

    classroomPhotoUrl,
    teacherProfilePhotoUrl,
    formattedTeacherName,

    fulfillmentTrailer,
    projectCostToComplete,
    totalProjectCost,
    onSuccess,
) => {
    let ctx = initCanvas(canvas);

    const { logoUrl } = getUrlConstants();

    const img = new Image();
    img.src = '../../../../../images/hlhm2023/hlhm-ig-bkg.png';

    img.onload = () => {

        ctx.fillStyle = '#1B0260';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
    };

    let classroomPhotoPromise = promiseFromNullable(classroomPhotoUrl);
    let teacherProfilePhotoPromise = promiseFromNullable(teacherProfilePhotoUrl);
    let logoPromise = promiseFromNullable(logoUrl);


    // wrapping this in a setTimeout bc of the backgroud image being loaded on top
    setTimeout(() => {
    Promise.all([classroomPhotoPromise, teacherProfilePhotoPromise, logoPromise])
        .then((images) => {



            if (images[0] != null) {
                let classroomPhoto = images[0];
            }

            if (images[1] != null) {
                let teacherProfilePhoto = images[1];

                let x = scaleWidth(45, canvas);
                let y = scaleHeight(100, canvas);
                let radius = scaleWidth(25, canvas);

                drawImageInBorderedCircle(ctx, teacherProfilePhoto, x, y, radius);
            }

            if (images[2] != null) {
                let logo = images[2];

                let x = scaleWidth(17, canvas);
                let y = scaleHeight(350, canvas);
                let logoWidth = 76;
                let logoHeight = 29;

                ctx.drawImage(logo, x, y, logoWidth, logoHeight);

            }

            onSuccess(canvas);

        });




    const lineHeight = DEFAULT_LINE_HEIGHT;
    ctx.font = `${lineHeight}px Helvetica`;
    let formattedText = wrapText(ctx, fulfillmentTrailer, scaleWidth(17, canvas), lineHeight + scaleHeight(135, canvas), scaleWidth(250, canvas), Math.floor(lineHeight * TEXT_HEIGHT_MULTIPLIER));

    formattedText[0].forEach(function(item, index) {
        // Draw the background-rounded-rectangles.
        ctx.fillStyle = '#FF9D46';
        const textSize = ctx.measureText(item[0]);

        const horizontalPadding = Math.floor(lineHeight / 4);
        const x = Math.floor(item[1] - horizontalPadding);
        // Subtract lineHeight because rectangles specify upper left corner and text specifies bottom left
        const y = item[2] - lineHeight;

        // Last arg is rounding-radius. 0 = a normal rectangular corner. 0 is correct on lines other than the first and last.
        const upperLeftRadius = index === 0 ? 20 : 0;
        const lowerLeftRadius = (index === formattedText[0].length - 1) ? 20 : 0;
        hopefullyRoundRect(ctx, x, y, textSize.width + (2 * horizontalPadding), Math.floor(lineHeight * TEXT_HEIGHT_MULTIPLIER), [upperLeftRadius, 20, 20, lowerLeftRadius]);
        ctx.fill();
    });

    formattedText[0].forEach(function(item) {
        ctx.fillStyle = '#161253';
        ctx.fillText(item[0], item[1], item[2]);
    });
    }, 1000);
}


export const resetCanvas = (canvas) => {
    let ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
}

const getUrlConstants = () => {
    const logoUrl = '/images/misc/DCLogoW.png';
    const backgroundPhotoUrl = '/images/tapestry-wireframe-dark-blue@2x.png?auto=webp';

    return { logoUrl, backgroundPhotoUrl };
}
